import { inject, DestroyRef } from "@angular/core";
import { signalStore, withState, withComputed, withMethods, patchState, withHooks } from "@ngrx/signals";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { StateService, RouteHistory } from "../state.service";
import { Item } from "../../pojo/Item";

interface StateStoreState {
    currentStateName: string | null;
    routeHistory: RouteHistory[] | null;
}

export const StateStore = signalStore(
    { providedIn: "root" },
    withState<StateStoreState>({
        currentStateName: null,
        routeHistory: null,
    }),
    withComputed((store) => ({
        isStudentEngagementState: () => {
            const stateName = store.currentStateName();
            return !!stateName && ["se", "studentEngagement"].some((state) => stateName.startsWith(state));
        },
    })),
    withComputed((store) => ({
        currentSeState: () => {
            const stateName = store.currentStateName(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            return stateName;
        },
        seObjTypeId: () => {
            const stateName = store.currentStateName(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            const match = stateName?.match(/(Event|Organization)/);
            if (match) {
                const type = match[1];
                if (type === "Event") return Item.Ids.Event;
                else if (type === "Organization") return Item.Ids.Organization;
            }
            return null;
        },
        seItemId: () => {
            const routeHistory = store.routeHistory(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            const current = routeHistory?.at(-1)?.to;
            if (typeof current === "object" && current?._params?.itemId) {
                return Number(current._params.itemId);
            }
            return null;
        },
        seOrgTab: () => {
            const routeHistory = store.routeHistory(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            const current = routeHistory?.at(-1)?.to;
            if (typeof current === "object" && current?._params?.tab) {
                return current._params.tab as string;
            }
            return null;
        },
        seElectionId: () => {
            const routeHistory = store.routeHistory(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            const current = routeHistory?.at(-1)?.to;
            if (typeof current === "object" && current?._params?.electionId) {
                const electionId = Number(current._params.electionId);
                return Number.isFinite(electionId) ? electionId : null;
            }
            return null;
        },
        seEmailTemplateId: () => {
            const routeHistory = store.routeHistory(),
                isStudentEngagementState = store.isStudentEngagementState();
            if (!isStudentEngagementState) return null;
            const current = routeHistory?.at(-1)?.to;
            if (typeof current === "object" && current?._params?.emailTemplateId != null) {
                return String(current._params.emailTemplateId);
            }
            return null;
        },
    })),
    withMethods((store) => ({
        _updateFromTransition(): void {
            const routeHistory: RouteHistory[] = StateService.getRouteHistory();
            patchState(store, {
                currentStateName: StateService.getRouteToIdentifier(routeHistory?.at(-1)),
                routeHistory: routeHistory ?? null,
            });
        },
    })),
    withHooks({
        onInit(store, destroyRef = inject(DestroyRef)) {
            store._updateFromTransition();
            const transitionSuccess$ = new Observable<void>((subscriber) => {
                const $rootScope = window.angBridge?.$injector?.get("$rootScope");
                if (!$rootScope) return;
                const deregisterFn = $rootScope.$on("$stickyTransitionSuccess", () => subscriber.next());
                return () => deregisterFn();
            });

            transitionSuccess$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
                store._updateFromTransition();
            });
        },
    }),
);
